// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventContextAction, ProjectItemList } from '@/audit/api/types';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography } from '@mui/material';

interface ProjectItemDifference {
  id: string;
  name: string;
  oldCount: number | null;
  newCount: number | null;
}

interface Props {
  property: ProjectItemList;
  action: AuditEventContextAction;
}

export function AuditProjectItemListPropertyLabel(props: Props) {
  const items: ProjectItemDifference[] = [];

  props.property.oldValue?.forEach(({ id, name, count }) => {
    items.push({ id, name, oldCount: count, newCount: null });
  });

  props.property.newValue.forEach(({ id, name, count }) => {
    const existing = items.find((item) => item.id === id);

    if (existing === undefined) {
      return items.push({ id, name, oldCount: null, newCount: count });
    }

    existing.newCount = count;
  });

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {items.map(({ id, name, oldCount, newCount }) => {
        if (oldCount === newCount) {
          return null;
        }

        if (oldCount === null) {
          return (
            <Stack key={id}>
              <Typography variant="caption" marginLeft={2}>
                <Typography component="span" variant="inherit">
                  {name}
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ textDecoration: 'underline' }}
                >
                  added
                </Typography>
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Quantity:&nbsp;{newCount}
              </Typography>
            </Stack>
          );
        }

        if (newCount === null) {
          return (
            <Stack key={id}>
              <Typography variant="caption" marginLeft={2}>
                <Typography component="span" variant="inherit">
                  {name}
                </Typography>
                {props.action !== 'DELETE' && (
                  <>
                    {' '}
                    <Typography
                      component="span"
                      variant="inherit"
                      sx={{ textDecoration: 'underline' }}
                    >
                      removed
                    </Typography>
                  </>
                )}
              </Typography>
              <Typography variant="caption" marginLeft={4}>
                Quantity:&nbsp;{oldCount}
              </Typography>
            </Stack>
          );
        }

        return (
          <Stack key={id}>
            <Typography variant="caption" marginLeft={2}>
              <Typography component="span" variant="inherit">
                {name}
              </Typography>{' '}
              <Typography
                component="span"
                variant="inherit"
                sx={{ textDecoration: 'underline' }}
              >
                updated
              </Typography>
            </Typography>
            <Typography
              variant="caption"
              marginLeft={4}
              display="flex"
              alignItems="center"
            >
              Quantity:&nbsp;{oldCount}
              <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
              {newCount}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}
